import { Contact, DraftPermissionRow, RowDirtyFlags } from "@manageContacts/models";
import {
  ApiContact,
  ManageContactsEditRequest,
  ManageContactsEditRow,
  ManageContactsEditWireRequest,
  ManageContactsEditWireRow,
} from "@manageContacts/store/models";
import { mapDocumentAccessToDisplay, mapNotificationToDisplay } from "@manageContacts/constants";

/**
 * Compare two string[] for set-equality (order-insensitive). Empty arrays compare equal.
 * Used to detect cell-level dirty state for a permission field.
 */
export const arraysEqualAsSet = (a: string[] | undefined, b: string[] | undefined): boolean => {
  const left = a ?? [];
  const right = b ?? [];

  if (left.length !== right.length) return false;
  if (left.length === 0) return true;

  const seen = new Set(left);

  for (const value of right) {
    if (!seen.has(value)) return false;
  }

  return true;
};

/**
 * Compare two DraftPermissionRow snapshots and return true when every field
 * (roles, documentAccess, notifications) is set-equal.
 */
export const rowsEqual = (a: DraftPermissionRow | undefined, b: DraftPermissionRow | undefined): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;

  return (
    arraysEqualAsSet(a.roles, b.roles) &&
    arraysEqualAsSet(a.documentAccess, b.documentAccess) &&
    arraysEqualAsSet(a.notifications, b.notifications)
  );
};

/**
 * Per-field dirty flags for a given (draft, original) pair. Used to drive
 * cell-level background highlighting (#EBF3FB) and option-level highlighting
 * (#D8E8F7) in edit mode.
 */
export const getRowDirtyFlags = (
  draft: DraftPermissionRow,
  original: DraftPermissionRow | undefined
): RowDirtyFlags => {
  const baseline = original ?? { roles: [], documentAccess: [], notifications: [] };

  return {
    roles: !arraysEqualAsSet(draft.roles, baseline.roles),
    documentAccess: !arraysEqualAsSet(draft.documentAccess, baseline.documentAccess),
    notifications: !arraysEqualAsSet(draft.notifications, baseline.notifications),
  };
};

/**
 * Apply the Notification gating rule:
 *
 *  - Notifications are only valid when their corresponding Document Access permission is selected
 *    (allowed-set = next.documentAccess, including unsaved selections).
 *  - Whenever a Document Access permission is selected, the matching Notification is auto-selected
 *    (deduped). This is the simplest mental model for end users: if you can see the document, you
 *    can also receive notifications about it. Users can still uncheck the notification manually.
 *  - When a Document Access permission is removed, the matching Notification is auto-removed.
 *  - All other Notification selections are preserved as long as they remain allowed.
 *
 * The `baseline` parameter is accepted for backward compatibility with existing call sites but is
 * not consulted; the rule above does not depend on baseline state.
 *
 * The function is pure and order-preserves the previous selections so re-derivations are stable.
 */
export const deriveNotifications = (
  next: DraftPermissionRow,
  prev: DraftPermissionRow,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  baseline?: DraftPermissionRow
): string[] => {
  const allowed = new Set(next.documentAccess);
  const kept = prev.notifications.filter((n) => allowed.has(n));
  const merged: string[] = [...kept];

  for (const value of next.documentAccess) {
    if (!merged.includes(value)) {
      merged.push(value);
    }
  }

  return merged;
};

/**
 * Build the per-row save payload. Emits a `Remove` row for each row marked for removal
 * (old* = current permissions for the audit trail, new* = empty) and a `PermissionEdit` row
 * for each row whose draft differs from its baseline. Removal wins: a row marked for removal
 * is never also emitted as an edit.
 */
export const buildEditRowDiff = (
  contacts: Contact[],
  originalRowMap: Record<string, DraftPermissionRow>,
  draftRowMap: Record<string, DraftPermissionRow>,
  removedRowIds: string[] = []
): ManageContactsEditRow[] => {
  const result: ManageContactsEditRow[] = [];
  const removed = new Set(removedRowIds);
  const byId = new Map(contacts.map((c) => [c.id, c]));

  const baseRow = (
    contact: Contact
  ): Omit<
    ManageContactsEditRow,
    | "changeType"
    | "newRoles"
    | "newDocumentAccessIds"
    | "newNotificationIds"
    | "oldRoles"
    | "oldDocumentAccessIds"
    | "oldNotificationIds"
  > => ({
    sfdcContactId: contact.sfdcContactId ?? contact.id.split("-")[0],
    accountGlobalId: contact.accountGlobalId,
    fundGlobalId: contact.fundGlobalId ?? "",
    fundId: contact.fundId,
  });

  // Removals first. old* carries the contact's current permissions so the audit logs a clear.
  for (const rowId of removedRowIds) {
    const contact = byId.get(rowId);

    if (!contact) continue;

    const original = originalRowMap[rowId];

    result.push({
      ...baseRow(contact),
      changeType: "Remove",
      oldRoles: original?.roles ?? contact.roles,
      newRoles: [],
      oldDocumentAccessIds: original?.documentAccess ?? contact.documentAccess,
      newDocumentAccessIds: [],
      oldNotificationIds: original?.notifications ?? contact.notifications,
      newNotificationIds: [],
    });
  }

  // Permission edits for dirty rows that are not being removed.
  for (const contact of contacts) {
    if (removed.has(contact.id)) continue;

    const draft = draftRowMap[contact.id];
    const original = originalRowMap[contact.id];

    if (!draft || !original) continue;
    if (rowsEqual(draft, original)) continue;

    result.push({
      ...baseRow(contact),
      changeType: "PermissionEdit",
      oldRoles: original.roles,
      newRoles: draft.roles,
      oldDocumentAccessIds: original.documentAccess,
      newDocumentAccessIds: draft.documentAccess,
      oldNotificationIds: original.notifications,
      newNotificationIds: draft.notifications,
    });
  }

  return result;
};

/**
 * Snapshot a Contact[] list into a DraftPermissionRow map, keyed by Contact.id. The
 * returned map is structurally cloned (arrays copied) so callers may mutate copies
 * without aliasing the original.
 */
export const snapshotContacts = (contacts: Contact[]): Record<string, DraftPermissionRow> => {
  const result: Record<string, DraftPermissionRow> = {};

  for (const contact of contacts) {
    result[contact.id] = {
      roles: [...contact.roles],
      documentAccess: [...contact.documentAccess],
      notifications: [...contact.notifications],
    };
  }

  return result;
};

export interface PermissionIdLookups {
  /** Display string (e.g. "Capital Call") → backend `serviceId`. */
  docIdMap: Record<string, number>;
  /** Display string (e.g. "Capital Call") → backend `notificationId`. */
  notifIdMap: Record<string, number>;
}

/**
 * Walk the contacts GET response and build display-string → numeric-ID lookups
 * for Document Access (`serviceId`) and Notifications (`notificationId`). The
 * lookups are needed because the save endpoint expects numeric IDs, while the
 * page's edit-mode state machine carries display strings.
 *
 * Same display value may appear on many funds; the first non-falsy ID wins.
 * Entries with id 0 / missing display are skipped.
 */
export const buildPermissionIdLookups = (contacts: ApiContact[]): PermissionIdLookups => {
  const docIdMap: Record<string, number> = {};
  const notifIdMap: Record<string, number> = {};

  for (const contact of contacts) {
    const funds = contact.account?.funds ?? [];

    for (const fund of funds) {
      for (const doc of fund.documentAccess ?? []) {
        const rawLabel = doc.displayName || doc.serviceName;

        if (!rawLabel || !doc.serviceId) continue;

        const key = mapDocumentAccessToDisplay(rawLabel);

        if (key && !(key in docIdMap)) {
          docIdMap[key] = doc.serviceId;
        }
      }

      for (const notif of fund.notifications ?? []) {
        const rawLabel = notif.displayName || notif.notificationName;

        if (!rawLabel || !notif.notificationId) continue;

        const key = mapNotificationToDisplay(rawLabel);

        if (key && !(key in notifIdMap)) {
          notifIdMap[key] = notif.notificationId;
        }
      }
    }
  }

  return { docIdMap, notifIdMap };
};

/**
 * Translate a single internal edit row into the wire shape. Display strings are
 * resolved to numeric IDs via the supplied lookups; values that can't be resolved
 * are dropped (and a console warning is emitted so they surface during development).
 */
const translateRow = (row: ManageContactsEditRow, lookups: PermissionIdLookups): ManageContactsEditWireRow => {
  const resolve = (
    values: string[],
    map: Record<string, number>,
    kind: "documentAccess" | "notification"
  ): number[] => {
    const ids: number[] = [];

    for (const value of values) {
      const id = map[value];

      if (typeof id === "number") {
        ids.push(id);
      } else {
        console.warn(
          `[Manage Contacts] No ${kind} id mapping for "${value}" on row ${row.sfdcContactId}/${row.fundGlobalId}; omitting from save payload.`
        );
      }
    }

    return ids;
  };

  return {
    sfdcContactId: row.sfdcContactId,
    accountGlobalId: row.accountGlobalId,
    fundGlobalId: row.fundGlobalId,
    fundId: row.fundId,
    changeType: row.changeType,
    oldRoles: row.oldRoles,
    newRoles: row.newRoles,
    oldDocumentAccessIds: resolve(row.oldDocumentAccessIds, lookups.docIdMap, "documentAccess"),
    newDocumentAccessIds: resolve(row.newDocumentAccessIds, lookups.docIdMap, "documentAccess"),
    oldNotificationIds: resolve(row.oldNotificationIds, lookups.notifIdMap, "notification"),
    newNotificationIds: resolve(row.newNotificationIds, lookups.notifIdMap, "notification"),
  };
};

/**
 * Convert the internal `ManageContactsEditRequest` (display strings, `changedRows`)
 * into the wire-shape `ManageContactsEditWireRequest` (numeric IDs, `rows`) that
 * `POST contacts/v1/user/ManageContacts/Permissions` expects.
 */
export const toEditWireRequest = (
  request: ManageContactsEditRequest,
  lookups: PermissionIdLookups
): ManageContactsEditWireRequest => ({
  rows: request.changedRows.map((row) => translateRow(row, lookups)),
});
